import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';

const StoriesContext = createContext(null);

const getStoryId = (story) => story?._id || story?.id;

export const StoriesProvider = ({ children }) => {
  const [stories, setStories] = useState([]);
  const [featuredStories, setFeaturedStories] = useState([]);
  const [storyCache, setStoryCache] = useState({});

  const [storiesLoading, setStoriesLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);

  const [storiesError, setStoriesError] = useState(null);
  const [featuredError, setFeaturedError] = useState(null);
  const [storyError, setStoryError] = useState(null);

  const storyCacheRef = useRef({});
  const storiesRef = useRef([]);
  const featuredStoriesRef = useRef([]);
  const inFlightStoryRequestsRef = useRef(new Map());

  useEffect(() => {
    storyCacheRef.current = storyCache;
  }, [storyCache]);

  useEffect(() => {
    storiesRef.current = stories;
  }, [stories]);

  useEffect(() => {
    featuredStoriesRef.current = featuredStories;
  }, [featuredStories]);

  const warmStory = useCallback((story) => {
    const id = getStoryId(story);
    if (!id || !story) return;
    setStoryCache((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...story },
    }));
  }, []);

  const getStoryFromCache = useCallback(
    (id) => (id ? storyCache[id] || null : null),
    [storyCache]
  );

  const fetchStories = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;

    if (!force && storiesRef.current.length > 0 && Object.keys(params).length === 0) {
      return storiesRef.current;
    }

    setStoriesLoading(true);
    setStoriesError(null);

    try {
      const response = await apiService.stories.getAll(params);
      const list = response.data?.data || response.data?.stories || response.data || [];
      setStories(list);

      setStoryCache((prev) => {
        const next = { ...prev };
        list.forEach((item) => {
          const id = getStoryId(item);
          if (id) next[id] = { ...(next[id] || {}), ...item };
        });
        return next;
      });

      return list;
    } catch (error) {
      setStoriesError(error);
      throw error;
    } finally {
      setStoriesLoading(false);
    }
  }, []);

  const fetchFeaturedStories = useCallback(async (options = {}) => {
    const { force = false } = options;

    if (!force && featuredStoriesRef.current.length > 0) {
      return featuredStoriesRef.current;
    }

    setFeaturedLoading(true);
    setFeaturedError(null);

    try {
      const response = await apiService.stories.getFeatured();
      const list = response.data?.stories || response.data?.data || response.data || [];
      setFeaturedStories(list);

      setStoryCache((prev) => {
        const next = { ...prev };
        list.forEach((item) => {
          const id = getStoryId(item);
          if (id) next[id] = { ...(next[id] || {}), ...item };
        });
        return next;
      });

      return list;
    } catch (error) {
      setFeaturedError(error);
      throw error;
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  const fetchStoryById = useCallback(async (id, options = {}) => {
    const { force = false, prefill = null } = options;

    if (!id) return null;

    if (prefill) {
      warmStory(prefill);
    }

    const cached = storyCacheRef.current[id];
    if (!force && cached && cached.content) {
      return cached;
    }

    const inFlight = inFlightStoryRequestsRef.current.get(id);
    if (!force && inFlight) {
      return inFlight;
    }

    setStoryLoading(true);
    setStoryError(null);

    try {
      const request = apiService.stories
        .getById(id)
        .then((response) => {
          const fullStory = response.data?.story || response.data?.data || response.data;

          setStoryCache((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), ...fullStory },
          }));

          return fullStory;
        })
        .finally(() => {
          inFlightStoryRequestsRef.current.delete(id);
        });

      inFlightStoryRequestsRef.current.set(id, request);
      return await request;
    } catch (error) {
      setStoryError(error);
      throw error;
    } finally {
      setStoryLoading(false);
    }
  }, [warmStory]);

  const value = useMemo(() => ({
    stories,
    featuredStories,
    storyCache,
    storiesLoading,
    featuredLoading,
    storyLoading,
    storiesError,
    featuredError,
    storyError,
    fetchStories,
    fetchFeaturedStories,
    fetchStoryById,
    warmStory,
    getStoryFromCache,
  }), [
    stories,
    featuredStories,
    storyCache,
    storiesLoading,
    featuredLoading,
    storyLoading,
    storiesError,
    featuredError,
    storyError,
    fetchStories,
    fetchFeaturedStories,
    fetchStoryById,
    warmStory,
    getStoryFromCache,
  ]);

  return <StoriesContext.Provider value={value}>{children}</StoriesContext.Provider>;
};

export const useStories = () => {
  const context = useContext(StoriesContext);
  if (!context) {
    throw new Error('useStories must be used within a StoriesProvider');
  }
  return context;
};
